/**
 * As opcoes de query e de mutation, prontas para `useQuery` / `useMutation`.
 *
 * Sao **fabricas puras**: recebem o cliente, devolvem objetos. Nao importam React, nao usam
 * hook e sao testaveis sem renderizar nada — o que importa aqui e a chave, o `queryFn` e a
 * politica de cache, e nenhuma das tres precisa de arvore de componentes para ser verificada.
 *
 * `hooks.tsx` embrulha isto em `useQuery` para quem prefere `useProduto(slug)` a
 * `useQuery(queries.catalog.product(slug, storeId))`. As duas formas chamam este arquivo.
 *
 * ## O `signal` nao e opcional por acidente
 *
 * Todo `queryFn` repassa o `AbortSignal` que o TanStack Query fornece. Sem isso, sair de uma
 * tela nao cancela a requisicao — e no perfil-alvo (Android de entrada, 4G instavel — `PWA
 * §0.1`) requisicao orfa e franquia gasta e uma conexao a menos para a que importa.
 */
import type {
  AddressListResponse,
  AvailabilityResponse,
  CartResponse,
  CategoryTreeResponse,
  DeliveryZonesResponse,
  MyOrdersResponse,
  OrderDetail,
  ProductDetail,
  ProductListResponse,
  QuoteRequest,
  QuoteResponse,
  SearchResponse,
  StoreDetail,
  StoreListResponse,
  StoreQueueResponse,
  StoreSummaryResponse,
  SuggestResponse,
} from '@farmacia/contracts';

import type { ApiClient } from '../client.js';
import type {
  AvailabilityInput,
  ProductListInput,
  SearchInput,
  SuggestInput,
} from '../endpoints/public.js';
import type { MyOrdersInput } from '../endpoints/orders.js';
import type { StoreQueueInput, StoreSummaryInput } from '../endpoints/store.js';

import { cachePolicyFor, catalogCachePolicy } from './cache-policy.js';
import { queryKeys, type QueryKey } from './keys.js';

/**
 * A forma minima de `UseQueryOptions` que este pacote produz.
 *
 * Deliberadamente **estrutural**, e nao `UseQueryOptions` do `@tanstack/react-query`: o pacote
 * declara React Query como peer **opcional** (o service worker e a fila offline usam o cliente
 * sem React). Amarrar o tipo destas fabricas ao tipo do React Query obrigaria a dependencia a
 * existir para o pacote sequer tipar.
 */
export interface QueryOptions<TData> {
  readonly queryKey: QueryKey;
  readonly queryFn: (context: { readonly signal: AbortSignal }) => Promise<TData>;
  readonly staleTime: number;
  readonly gcTime: number;
}

export interface MutationOptions<TVariables, TData> {
  readonly mutationKey: QueryKey;
  readonly mutationFn: (variables: TVariables) => Promise<TData>;
}

export interface ApiQueries {
  readonly stores: {
    list: () => QueryOptions<StoreListResponse>;
    bySlug: (slug: string) => QueryOptions<StoreDetail>;
  };
  readonly catalog: {
    categories: () => QueryOptions<CategoryTreeResponse>;
    /** `hasSession` decide a politica: com sessao o catalogo e `no-store` (`API §8.1`). */
    products: (query: ProductListInput, hasSession: boolean) => QueryOptions<ProductListResponse>;
    product: (
      slug: string,
      storeId: string,
      hasSession: boolean,
    ) => QueryOptions<ProductDetail>;
    availability: (query: AvailabilityInput) => QueryOptions<AvailabilityResponse>;
    search: (query: SearchInput) => QueryOptions<SearchResponse>;
    suggest: (query: SuggestInput) => QueryOptions<SuggestResponse>;
  };
  readonly delivery: {
    zones: (storeId: string) => QueryOptions<DeliveryZonesResponse>;
  };
  readonly cart: {
    current: () => QueryOptions<CartResponse>;
  };
  readonly orders: {
    list: (query?: MyOrdersInput) => QueryOptions<MyOrdersResponse>;
    detail: (orderId: string) => QueryOptions<OrderDetail>;
  };
  readonly addresses: {
    list: () => QueryOptions<AddressListResponse>;
  };
  readonly storePanel: {
    queue: (query: StoreQueueInput) => QueryOptions<StoreQueueResponse>;
    summary: (query: StoreSummaryInput) => QueryOptions<StoreSummaryResponse>;
  };
  readonly mutations: {
    quote: () => MutationOptions<QuoteRequest, QuoteResponse>;
  };
}

export function createQueries(client: ApiClient): ApiQueries {
  const publicPolicy = {
    stores: cachePolicyFor('public.stores'),
    zones: cachePolicyFor('public.zones'),
    categories: cachePolicyFor('public.categories'),
  };
  const availabilityPolicy = cachePolicyFor('catalog.availability');
  const searchPolicy = cachePolicyFor('catalog.search');
  const cartPolicy = cachePolicyFor('cart');
  const ordersPolicy = cachePolicyFor('orders');
  const addressesPolicy = cachePolicyFor('me.addresses');
  const queuePolicy = cachePolicyFor('store.queue');
  const counterPolicy = cachePolicyFor('store.counter');

  return {
    stores: {
      list: () => ({
        queryKey: queryKeys.public.stores(),
        queryFn: ({ signal }) => client.stores.list(signal),
        staleTime: publicPolicy.stores.staleTime,
        gcTime: publicPolicy.stores.gcTime,
      }),
      bySlug: (slug) => ({
        queryKey: queryKeys.public.store(slug),
        queryFn: ({ signal }) => client.stores.bySlug(slug, signal),
        staleTime: publicPolicy.stores.staleTime,
        gcTime: publicPolicy.stores.gcTime,
      }),
    },

    catalog: {
      categories: () => ({
        queryKey: queryKeys.public.categories(),
        queryFn: ({ signal }) => client.catalog.categories(signal),
        staleTime: publicPolicy.categories.staleTime,
        gcTime: publicPolicy.categories.gcTime,
      }),
      products: (query, hasSession) => {
        const policy = catalogCachePolicy(hasSession);
        return {
          queryKey: queryKeys.catalog.products({ ...query }),
          queryFn: ({ signal }) => client.catalog.products(query, signal),
          staleTime: policy.staleTime,
          gcTime: policy.gcTime,
        };
      },
      product: (slug, storeId, hasSession) => {
        const policy = catalogCachePolicy(hasSession);
        return {
          queryKey: queryKeys.catalog.product(slug, storeId),
          queryFn: ({ signal }) => client.catalog.product(slug, { storeId }, signal),
          staleTime: policy.staleTime,
          gcTime: policy.gcTime,
        };
      },
      availability: (query) => ({
        queryKey: queryKeys.catalog.availability(query.storeId, query.skus),
        queryFn: ({ signal }) => client.catalog.availability(query, signal),
        // `no-store` no contrato: "Estoque nunca e cacheado. Nem 5 s" (`API §8.1`).
        staleTime: availabilityPolicy.staleTime,
        gcTime: availabilityPolicy.gcTime,
      }),
      search: (query) => ({
        queryKey: queryKeys.catalog.search({ ...query }),
        queryFn: ({ signal }) => client.catalog.search(query, signal),
        // Termo de busca e potencialmente dado de saude (`API §6.7`): nada em disco, nada em
        // memoria depois que a tela fecha.
        staleTime: searchPolicy.staleTime,
        gcTime: searchPolicy.gcTime,
      }),
      suggest: (query) => ({
        queryKey: queryKeys.catalog.suggest(query.q, query.storeId),
        queryFn: ({ signal }) => client.catalog.suggest(query, signal),
        staleTime: searchPolicy.staleTime,
        gcTime: searchPolicy.gcTime,
      }),
    },

    delivery: {
      zones: (storeId) => ({
        queryKey: queryKeys.public.zones(storeId),
        queryFn: ({ signal }) => client.delivery.zones({ storeId }, signal),
        staleTime: publicPolicy.zones.staleTime,
        gcTime: publicPolicy.zones.gcTime,
      }),
    },

    cart: {
      current: () => ({
        queryKey: queryKeys.cart.current(),
        queryFn: ({ signal }) => client.cart.get(signal),
        staleTime: cartPolicy.staleTime,
        gcTime: cartPolicy.gcTime,
      }),
    },

    orders: {
      list: (query) => ({
        queryKey: queryKeys.orders.list({ ...query }),
        queryFn: ({ signal }) => client.orders.mine(query, signal),
        staleTime: ordersPolicy.staleTime,
        gcTime: ordersPolicy.gcTime,
      }),
      detail: (orderId) => ({
        queryKey: queryKeys.orders.detail(orderId),
        queryFn: ({ signal }) => client.orders.byId(orderId, signal),
        staleTime: ordersPolicy.staleTime,
        gcTime: ordersPolicy.gcTime,
      }),
    },

    addresses: {
      list: () => ({
        queryKey: queryKeys.addresses.list(),
        queryFn: ({ signal }) => client.addresses.list(signal),
        staleTime: addressesPolicy.staleTime,
        gcTime: addressesPolicy.gcTime,
      }),
    },

    storePanel: {
      queue: (query) => ({
        queryKey: queryKeys.storePanel.queue({ ...query }),
        queryFn: ({ signal }) => client.storePanel.queue(query, signal),
        staleTime: queuePolicy.staleTime,
        gcTime: queuePolicy.gcTime,
      }),
      summary: (query) => ({
        queryKey: queryKeys.storePanel.summary({ ...query }),
        queryFn: ({ signal }) => client.storePanel.summary(query, signal),
        staleTime: counterPolicy.staleTime,
        gcTime: counterPolicy.gcTime,
      }),
    },

    mutations: {
      /**
       * A cotacao e **mutation**, e nao query, e isso e decisao de contrato, nao de estilo:
       * `POST /v1/checkout/quote` cria uma cotacao com `quoteId`, `quoteHash` e TTL de 10 min
       * (`CONTRACTS §6`). Guardar o resultado numa chave de query seria guardar um preco que
       * vence — o oposto do que `PWA §4.2 R12` manda ("nunca em disco, em lugar nenhum").
       */
      quote: () => ({
        mutationKey: ['checkout', 'quote'],
        mutationFn: (body: QuoteRequest) => client.checkout.quote(body),
      }),
    },
  };
}
